import {
  ref,
  get,
  set,
  update,
  push,
  remove,
  onValue,
  query,
  orderByChild,
  limitToLast,
  type Query,
  type DataSnapshot,
} from 'firebase/database';
import type { ZodType } from 'zod';

import { db } from './firebase';

/* =====================================================================
   firebaseClient — punto único de entrada para Realtime Database.
   Equivalente al `ApiService` de la guía v2 § 10.6.

   Reglas:
   - Toda lectura/escritura a RTDB pasa por aquí.
   - Los hooks de TanStack Query consumen `readOnce` / `subscribe`.
   - Los formularios escriben con `writeUpdate` (parcial) o `pushNew`.
===================================================================== */

export class FirebaseError extends Error {
  readonly path: string;
  readonly cause?: unknown;
  constructor(message: string, path: string, cause?: unknown) {
    super(message);
    this.name = 'FirebaseError';
    this.path = path;
    this.cause = cause;
  }
}

/* ------------------------------------------------------------------ */
/*  Internal: parsear un snapshot tipado                              */
/* ------------------------------------------------------------------ */

function parseSnapshot<T>(snap: DataSnapshot, schema: ZodType<T>, path: string): T {
  const raw = snap.val();
  const result = schema.safeParse(raw);
  if (!result.success) {
    throw new FirebaseError(
      `Schema validation failed at "${path}": ${result.error.message}`,
      path,
      result.error,
    );
  }
  return result.data;
}

/* ------------------------------------------------------------------ */
/*  Lectura puntual (one-shot)                                        */
/* ------------------------------------------------------------------ */

export async function readOnce<T>(path: string, schema: ZodType<T>): Promise<T> {
  try {
    const snap = await get(ref(db, path));
    return parseSnapshot(snap, schema, path);
  } catch (err) {
    if (err instanceof FirebaseError) throw err;
    throw new FirebaseError(`Failed to read "${path}"`, path, err);
  }
}

/* ------------------------------------------------------------------ */
/*  Suscripción live (onValue)                                        */
/*  Devuelve la función de cleanup para `useEffect`.                  */
/* ------------------------------------------------------------------ */

export function subscribe<T>(
  path: string,
  schema: ZodType<T>,
  onData: (value: T) => void,
  onError?: (err: FirebaseError) => void,
): () => void {
  const r = ref(db, path);
  const unsub = onValue(
    r,
    (snap) => {
      try {
        onData(parseSnapshot(snap, schema, path));
      } catch (err) {
        if (err instanceof FirebaseError && onError) onError(err);
        else if (onError) onError(new FirebaseError(`Subscribe error at "${path}"`, path, err));
      }
    },
    (err) => {
      if (onError) onError(new FirebaseError(`Subscribe error at "${path}"`, path, err));
    },
  );
  return unsub;
}

/* ------------------------------------------------------------------ */
/*  Escritura completa (set) — sustituye el nodo                      */
/* ------------------------------------------------------------------ */

export async function writeSet<T>(path: string, value: T): Promise<void> {
  try {
    await set(ref(db, path), value);
  } catch (err) {
    throw new FirebaseError(`Failed to set "${path}"`, path, err);
  }
}

/* ------------------------------------------------------------------ */
/*  Escritura parcial (update) — sólo los paths del objeto            */
/*  Ej: update('/players/abc', { 'scores/g1': 4164, 'gPlayed/g1': true }) */
/* ------------------------------------------------------------------ */

export async function writeUpdate(path: string, updates: Record<string, unknown>): Promise<void> {
  try {
    await update(ref(db, path), updates);
  } catch (err) {
    throw new FirebaseError(`Failed to update "${path}"`, path, err);
  }
}

/* ------------------------------------------------------------------ */
/*  Push nuevo nodo (auto-id) — devuelve la id generada               */
/* ------------------------------------------------------------------ */

export async function pushNew<T>(path: string, value: T): Promise<string> {
  try {
    const r = push(ref(db, path));
    await set(r, value);
    if (!r.key) throw new Error('push() returned no key');
    return r.key;
  } catch (err) {
    throw new FirebaseError(`Failed to push to "${path}"`, path, err);
  }
}

/* ------------------------------------------------------------------ */
/*  Borrar nodo                                                       */
/* ------------------------------------------------------------------ */

export async function removeNode(path: string): Promise<void> {
  try {
    await remove(ref(db, path));
  } catch (err) {
    throw new FirebaseError(`Failed to remove "${path}"`, path, err);
  }
}

/* ------------------------------------------------------------------ */
/*  Query ordenada (top-N por campo) — usado para leaderboard         */
/* ------------------------------------------------------------------ */

export function topByField(path: string, field: string, limit: number): Query {
  return query(ref(db, path), orderByChild(field), limitToLast(limit));
}
